import { useEffect, useState } from "react";
import "./TransitView.scss";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom";
import { addError } from "../../utils/analytics";
import {
  getAwbShipmemtDetails,
  getShipmentDetailsByCode,
} from "../../services/transportationPlanServices";
import LoadingIndicator from "../LoadingIndicator";
import { useTranslation } from "react-i18next";
import {
  formatShippingDescription,
  getShipmentViewDetailsFormatted,
} from "./TransitViewUtils";
import { Divider } from "../../common/Divider";
import cargoIcon from "../../assets/images/cargoIcon.svg";
import planeIcon from "../../assets/images/flightTakeOffIcon.svg";
import calendar from "../../assets/images/Calendar.svg";
import warningIcon from "../../assets/images/warning_filled.svg"
import cargoDetailsIcon from "../../assets/images/CargoDetailsIcon.svg";
import flightIcon from "../../assets/images/Bookwithmiles.png";
import { SelectButton } from "primereact/selectbutton";
import circleFullTp from "../../assets/images/circleFullTp.svg";
import circleNoneTp from "../../assets/images/circleNoneTp.svg";
import circleHalfTp from "../../assets/images/circleHalfTp.svg";

const TransitView = () => {
  const { t ,i18n} = useTranslation(["labels", "errors"]);
  const isRTL = i18n.language === 'ar' || i18n.dir() === 'rtl';
  const history = useHistory();

  const location = useLocation();
  const awbNumber = location.state?.awbNumber;

  const [awbViewData, setAwbViewData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState("Actual");

  const backButtonClickHandler = (isError) => {
    history.push("/shipment-tracking/list", isError ? { isError } : null);
  };

  const tabValues = [
    { label: t("DASHBOARD.ACTUAL"), value: "Actual" },
    { label: t("MY_SHIPMENTS.PLANNED"), value: "Planned" },
  ];

  const getCargoDetailsString = () => {
    if (!awbViewData) return "";

    const pieces = awbViewData.pieces;
    const piecesUnit = awbViewData.piecesUnit || "pcs";
    const weight = awbViewData.weight;
    const weightUnit = awbViewData.weightUnit || "kg";
    const volume = awbViewData.volume;
    const volumeUnit = awbViewData.volumeUnit || "cbm";

    let result = [];

    if (pieces !== undefined && pieces !== null && pieces !== "") {
      result.push(`${pieces} ${piecesUnit}`);
    }
    if (weight !== undefined && weight !== null && weight !== "") {
      result.push(`${weight} ${weightUnit}`);
    }
    if (volume !== undefined && volume !== null && volume !== "") {
      result.push(`${volume} ${volumeUnit}`);
    }

    return result.join(" - ");
  };

  const getStatusColor = () => {
    const toaStatus = awbViewData?.toaStatus;
    if (toaStatus) {
      const trimmedLowerCaseStatus = toaStatus?.trim()?.toLowerCase();
      if (trimmedLowerCaseStatus?.includes("delay")) {
        if (trimmedLowerCaseStatus?.includes("potential")) {
          return "orange";
        } else {
          return "red";
        }
      } else {
        return "green";
      }
    } else {
      return "";
    }
  };

  const getStatusPill = () =>
    awbViewData?.toaStatus ? (
      <div className={`awb-status-pill ${getStatusColor()}`}>
        <label>{awbViewData?.toaStatus}</label>
        {awbViewData?.toaStatusDelay && (
          <label className="red-color ml-1">
            ({awbViewData?.toaStatusDelay})
          </label>
        )}
      </div>
    ) : (
      <></>
    );

  const getIconByStatus = (circleStatus) => {
    switch (circleStatus) {
      case "outer-ring":
        return circleFullTp;
      case "inner-ring":
        return circleNoneTp;
      case "middle-ring":
        return circleHalfTp;
      default:
        return circleNoneTp;
    }
  };

  const fetchShipmentData = async (selectedTabValue) => {
    setIsLoading(true);
    try {
      await getAwbShipmemtDetails(
        awbNumber,
        selectedTabValue === "Planned"
      ).then(async (res) => {
        if (res?.data) {
          let fullAwbData = res.data;
          const { commodity } = fullAwbData || {};
          if (commodity) {
            const modifyAwbData = (productData) => {
              fullAwbData = {
                ...fullAwbData,
                shipmentDesc: formatShippingDescription(
                  productData,
                  fullAwbData
                ),
              };
            };
            await getShipmentDetailsByCode(commodity)
              .then((responseData) => {
                if (responseData?.data) {
                  const productDetails = responseData.data;
                  modifyAwbData(productDetails);
                } else {
                  modifyAwbData(null);
                }
              })
              .catch(() => {
                modifyAwbData(null);
              });
          }
          setAwbViewData(fullAwbData);
          setSelectedTab(selectedTabValue);
        } else {
          backButtonClickHandler(true);
        }
      });
    } catch (error) {
      console.log(error.message);
      addError("LOADING_AWB_SHIPMENT_FAILED", error);
      backButtonClickHandler(true);
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    (async () => fetchShipmentData("Actual"))();
    window?.scrollTo(0, 0);
  }, []);

  return (
    <div className={`tp-detail-view-wrapper ${isRTL ? 'rtl' : ''}`}>
      {isLoading ? <LoadingIndicator /> : <></>}
      <>
        <div className="shipment-transit-summary">
          <div className="details-left-section">
            <div className="details-left-top">
              <img className="cargo-img" alt="cargo-img" src={cargoIcon} />
              <div className="awb-status-row">
                <label className="awb-number">{awbViewData?.awbNumber}</label>
                <label className={`awb-status-pill green`}>
                  {awbViewData?.shipmentStatus}
                </label>
              </div>
            </div>
            <div className="org-des-shp-section">
              <div className="org-des-section">
                <img className="plane-icon" alt="plane-icon" src={planeIcon} />
                <label className="shp-info-label">
                  {awbViewData?.origin} - {awbViewData?.destination}
                </label>
              </div>
              <Divider isVertical />
              <div className="shpdate-section">
                <img
                  className="calender-icon"
                  alt="calender-icon"
                  src={calendar}
                />
                <label dir="ltr" className="shp-info-label">
                  {awbViewData?.shipmentDate}
                </label>
              </div>
            </div>
            <div className="cargo-detail-section margin-adjust">
              <img
                className="cargo-icon"
                alt="cargo-icon"
                src={cargoDetailsIcon}
              />
              <label className="shp-info-label">
                <span dir="ltr"> {getCargoDetailsString()}</span>
                <Divider coverage={"100%"} isVertical />
                <span>{awbViewData?.shipmentDesc}</span>
              </label>
            </div>
          </div>
          <Divider
            coverage={"80%"}
            isVertical
            wrapperClassName={"main-divider"}
          />
          <div className="details-right-section">
            <div className="toa-section">
              <div className="toa-header">
                <label className="shp-info-heading">
                  {t("MY_SHIPMENTS.TOA_LABEL")}
                </label>
              </div>
              <div className="actual-planned-time">
                <div className="time-details">
                 {/* <div
                    className={`time-details ${
                      awbViewData?.estimatedTimeOfAvailability ? "strike-text" : ""
                    }`}
                  > */}
                  <label className="shp-info-label key-label">
                    {t("MY_SHIPMENTS.PLNND_TIME")}
                  </label>
                  <label className="shp-info-label" dir="ltr">
                    : {awbViewData?.plannedTimeOfAvailability}
                  </label>
                </div>
                 {awbViewData?.estimatedTimeOfAvailability && (
                 <div className="time-details">
                  <label className="shp-info-label key-label">
                    {t("MY_SHIPMENTS.ESTIMATED_TIME")}
                  </label>
                  <label className={`shp-info-label`} dir="ltr">
                    : {awbViewData?.estimatedTimeOfAvailability}
                  </label>
                </div>
                 )}
                <div className="time-details">
                  <label className="shp-info-label key-label">
                    {t("MY_SHIPMENTS.ACTL_TIME")}
                  </label>
                  <label className={`shp-info-label`} dir="ltr">
                    : {awbViewData?.actualTimeOfAvailability}
                  </label>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center">{getStatusPill()}</div>
            {awbViewData?.toaComment && awbViewData?.toaComment.trim() !== "" && (
              <div className="time-details-warning">
                <img
                  src={warningIcon}
                  className="warning-img"
                  alt="warning"
                />
                <label className="shp-info-label">
                  {awbViewData?.toaComment}
                </label>
              </div>
            )}
          </div>
        </div>
        <div className="shipment-transit-view">
          <SelectButton
            className="common-select-button"
            value={selectedTab}
            options={tabValues}
            onChange={(e) => {
              if (e.value) {
                fetchShipmentData(e.value);
              }
            }}
          />
          {/* Timeline Section */}
          <div className="shipment-card-container">
            <div className="timeline">
              {getShipmentViewDetailsFormatted(awbViewData)?.map(
                (eachCardData, index) => (
                  <div
                    key={index}
                    className={`timeline-item  ${selectedTab === "Planned" && !eachCardData.isLastElement
                      ? "dotted-line"
                      : eachCardData?.colorSchemes?.lineStatus
                      }`}
                  >
                    <img
                      src={
                        selectedTab === "Planned"
                          ? circleNoneTp
                          : getIconByStatus(
                            eachCardData?.colorSchemes?.circleStatus
                          )
                      }
                      className={`timeline-dot`}
                    />
                    <div className="timeline-content">
                      <label className="status-label">
                        {eachCardData?.location}
                      </label>
                      <div className="cards-conatiner">
                        {eachCardData?.cards.map((card, cardIndex) => (
                          <div key={cardIndex} className="card">
                            <label
                              className={`awb-status-pill ${card?.badgeColor}`}
                            >
                              {card.status}
                            </label>
                            <Divider />
                            <div className="details-section">
                              <label className="description-section">
                                <img
                                  className="cargo-icon"
                                  alt="cargo-icon"
                                  src={cargoDetailsIcon}
                                />
                                <span dir="ltr">{card.description}</span>
                              </label>
                              {card?.dateTime && (
                                <label className={`description-section`}>
                                  <img
                                    className="calender-icon"
                                    alt="calender-icon"
                                    src={calendar}
                                  />
                                  <span dir="ltr">{card.dateTime}</span>
                                </label>
                              )}
                              {card?.flightValue && (
                                <label className={`description-section`}>
                                  <img
                                    className="flight-icon"
                                    alt="flight-icon"
                                    src={flightIcon}
                                  />
                                  {card?.flightValue}
                                </label>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* Back button */}
        <div className="mt-4">
          <button
            className="secondary-button"
            onClick={() => backButtonClickHandler(false)}
          >
            {t("COMMON.BACK_BUTTON")}
          </button>
        </div>
      </>
    </div>
  );
};

export default TransitView;
